import Link from "@whoppah/next/link";

import { PLP_SEO_LEAF_HEADING_SIZE } from "@/components/search/plp-seo/plp-seo.tokens";
import { Heading } from "@/ui/cva/Heading";

export type ChipLink = {
  href: string;
  title: string;
};

type LinkChipsProps = {
  /** Group heading, e.g. "By brand". Omit for an unlabelled row. */
  heading?: string;
  /** Outline level of the group heading; defaults to h3 (a section of the block). */
  headingAs?: "h3" | "h4";
  links: ChipLink[];
};

/**
 * A row of internal links (WHO-5534): plain brand-500 text links, underlined on hover, in a
 * wrapping row. Rendered as normal links rather than chips (Vlad, 09-23): chips took too much
 * space and felt heavy. Every anchor stays in the HTML, so the SEO value is unchanged.
 * 44px tall on mobile so they stay tappable, 32px from md up.
 * The group heading is 16px bold in grey-800 (review round 2), whatever its outline level.
 */
export const LinkChips = ({
  heading,
  headingAs = "h3",
  links,
}: LinkChipsProps) => {
  if (links.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      {heading && (
        <Heading
          as={headingAs}
          size={PLP_SEO_LEAF_HEADING_SIZE}
          className="text-grey-800"
        >
          {heading}
        </Heading>
      )}
      <ul className="flex flex-wrap gap-x-5 md:gap-y-1">
        {links.map(link => (
          <li key={link.href}>
            <Link href={link.href} prefetch={false} className="plp-link-chip">
              {link.title}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};
